import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faShieldHalved,
  faGlobe,
  faPenNib,
} from "@fortawesome/free-solid-svg-icons";

import Navigation from "../Shared/Navigation";
import Seo from "../../seo/Seo";
import { useT } from "../../i18n/I18nContext";
import LocalePicker from "../../i18n/LocalePicker";
import { useAuth } from "../../auth/AuthContext";
import { apiClient } from "../../util/apiClient";
import { logger } from "../../util/logger";
// Brand wordmark — mobile-only above the form. Sized between
// /login (h-20, 2 fields) and /signup (h-12, 5 fields) since
// this page has 4 fields + the shield-icon header block.
import logo from "../../assets/plumescript-logo-removebg.png";

// Standalone reset-password page that lands the user with their email
// + 6-digit code already filled in from the URL (the email's "Reset
// password" button links here). The user only has to type a new
// password and submit — the friction the original modal-only flow had
// (copy code, paste into modal, type new password) collapses to one
// step.
//
// Falls through to a graceful degraded state when ``email`` or
// ``token`` are missing from the query string — the user can paste
// them by hand and still complete the reset.
export default function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const initialToken = (params.get("token") || "").replace(/\D/g, "").slice(0, 6);

  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Strip the token out of the address bar after we read it so a
  // "View page source" / browser-history entry on a shared device
  // doesn't expose the live code. The form state retains it.
  useEffect(() => {
    if (initialToken) {
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;
    if (!email.trim() || token.length !== 6 || !password) return;
    if (password !== confirm) {
      toast.error(t("auth.reset.password_mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/api/auth/password-reset/confirm/", {
        email: email.trim(),
        token,
        new_password: password,
      });
      toast.success(t("auth.reset.confirmed"), { autoClose: 6000 });
      // Redirect target depends on auth state at submit time:
      //   * Anonymous (came from email link / Login → Forgot) →
      //     ``/login`` so they can sign in with the new password.
      //   * Already signed in (came from Settings → Forgot link) →
      //     ``/setting`` rather than ``/login`` (which is ``guestOnly``
      //     and would silently bounce them back to the home page,
      //     making the success toast feel like an error).
      const dest = authStatus === "signedIn" ? "/setting" : "/login";
      navigate(dest, { replace: true });
    } catch (err) {
      logger.warn("password_reset_confirm_failed", err?.message);
      const code = err?.response?.data?.error;
      const rules = err?.response?.data?.rules;
      if (code === "invalid_or_expired_token") {
        toast.error(t("auth.reset.invalid_token"));
      } else if (code === "weak_password" && Array.isArray(rules) && rules.length) {
        toast.error(`${t("auth.reset.weak_password")} ${rules.join(", ")}`);
      } else {
        toast.error(t("auth.reset.confirm_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    !!email.trim() &&
    token.length === 6 &&
    password.length >= 8 &&
    password === confirm;

  return (
    <>
      <Seo title={t("auth.reset.title")} path="/reset-password" noindex />
      <Navigation searchQuery="" setSearchQuery={() => {}} />
      <div className="relative min-h-[calc(100vh-72px)] bg-bg flex items-start justify-center px-4 py-10">
        <LocalePicker className="absolute top-3 right-4 z-10" />
        {/* Stack wrapper so the form card + the mobile-only
            reassurance strip below it land in a column rather
            than side-by-side under the parent's flex row. */}
        <div className="w-full max-w-md flex flex-col items-center">
        <div className="w-full bg-surface border border-border rounded-2xl shadow-soft p-6 sm:p-8">
          {/* Mobile-only brand mark, above the page header. Hidden
              on sm+ since the wider viewport already carries the
              brand via the navbar above the card. */}
          <Link
            to="/"
            aria-label="Plumescript"
            className="sm:hidden flex justify-center mb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
          >
            <img
              src={logo}
              alt=""
              aria-hidden="true"
              className="app-logo h-14 w-auto"
            />
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primaryMuted text-primary shrink-0">
              <FontAwesomeIcon icon={faShieldHalved} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-text">
                {t("auth.reset.page_title")}
              </h1>
              <p className="text-xs text-secondText">
                {t("auth.reset.page_subtitle")}
              </p>
            </div>
          </div>

          {/* Inputs share the same chrome as Login / SignUp / the
              Reset modal so the auth flow reads as one component
              vocabulary across all four surfaces. Labels are kept
              in the DOM via ``sr-only`` so screen readers still
              announce each field; the visual cue lives in the
              placeholder. */}
          <form onSubmit={submit} className="space-y-3.5" noValidate>
            <label className="block">
              <span className="sr-only">
                {t("auth.email_label")}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.email_label")}
                autoComplete="email"
                required
                className="w-full px-4 py-3 rounded-full border border-border bg-surface text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
              />
            </label>

            <label className="block">
              <span className="sr-only">
                {t("auth.reset.token_label")}
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={token}
                onChange={(e) =>
                  setToken(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
                autoFocus={!initialToken}
                required
                className="w-full px-4 py-3 rounded-full border border-border bg-surface text-text font-mono tracking-[0.4em] text-center text-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
              />
            </label>

            <label className="block">
              <span className="sr-only">
                {t("auth.reset.new_password_label")}
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.reset.new_password_label")}
                autoComplete="new-password"
                minLength={8}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
                autoFocus={!!initialToken}
                required
                className="w-full px-4 py-3 rounded-full border border-border bg-surface text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
              />
            </label>

            <label className="block">
              <span className="sr-only">
                {t("auth.reset.confirm_password_label")}
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t("auth.reset.confirm_password_label")}
                autoComplete="new-password"
                minLength={8}
                required
                className="w-full px-4 py-3 rounded-full border border-border bg-surface text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
              />
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className="app-button-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faKey} className="mr-2" />
              {submitting ? t("common.loading") : t("auth.reset.confirm")}
            </button>
          </form>

          <p className="mt-4 text-xs text-secondText text-center">
            {t("auth.reset.back_to_login_prefix")}{" "}
            <Link to="/login" className="text-primary">
              {t("auth.reset.back_to_login")}
            </Link>
          </p>
        </div>

        {/* Reassurance strip — mobile only. Same chip rhythm as
            /login (icon medallion above a small label) so the
            two pages read as siblings on phone widths. Sits
            below the form card, hidden at sm+ where the card
            already has enough breathing room not to feel empty. */}
        <ul className="sm:hidden mt-6 grid grid-cols-3 gap-3 text-center w-full">
          {[
            { icon: faShieldHalved, label: t("auth.feature.secure") },
            { icon: faGlobe, label: t("auth.feature.interactive") },
            { icon: faPenNib, label: t("auth.feature.friendly") },
          ].map((f) => (
            <li
              key={f.label}
              className="flex flex-col items-center gap-1.5"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary">
                <FontAwesomeIcon icon={f.icon} className="text-sm" />
              </span>
              <span className="text-[11px] text-secondText leading-tight">
                {f.label}
              </span>
            </li>
          ))}
        </ul>
        </div>
      </div>
    </>
  );
}
